import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  TrackByFunction,
  ViewChild,
} from '@angular/core';
import {
  ActivatedRoute,
  IsActiveMatchOptions,
  Router,
  UrlTree,
} from '@angular/router';

/**
 * Configuration for one tab inside `<app-navigation-tabs>`.
 *
 * Active-state highlighting (`btnChange` class) is driven by EITHER:
 *   - `routerLink` + `routerLinkActive` — the recommended pattern when the
 *     tab navigates to a real route.
 *   - `activeCondition` — for in-page tab switches with no routing. The
 *     parent component is responsible for keeping this in sync (e.g. on
 *     `(tabClick)` mutate the array so exactly one tab is active).
 *
 * Click handling: a tab with `routerLink` will navigate AND emit
 * `(tabClick)`. A tab without `routerLink` only emits `(tabClick)`. Do not
 * use both routerLink and an in-page tabClick handler that also navigates,
 * or the navigation will fire twice.
 *
 * A tab whose `routerLink` is a strict prefix of any sibling tab's URL
 * must set `routerLinkActiveOptions` to opt into exact-path matching;
 * otherwise Angular's default prefix-match will keep the parent tab
 * highlighted alongside the active sub-tab.
 */
export interface NavigationTab {
  label: string;
  /** Set to enable router-driven activation. Pair with routerLinkActive. */
  routerLink?: string | any[];
  /** Class to apply when the routerLink matches. Usually 'btnChange'. */
  routerLinkActive?: string;
  /**
   * Optional override for Angular's `routerLinkActive` match strictness.
   * Defaults to undefined, in which case Angular's built-in prefix match
   * applies. Pass the full `IsActiveMatchOptions` shape (preferred over
   * the legacy `{ exact: true }`) when query-param behavior must be
   * explicit.
   */
  routerLinkActiveOptions?: { exact: boolean } | IsActiveMatchOptions;
  iconClass?: string;
  value?: string;
  /**
   * Optional explicit automation testid for this tab's button. When set it
   * overrides the default `nav-tab-<value>` testid. Left undefined for tabs
   * that are happy with the generated default.
   */
  dataTestId?: string;
  helpTitle?: string;
  hidden?: boolean;
  /** Manual active flag, used when the tab has no routerLink. */
  activeCondition?: boolean;
  /**
   * Class(es) applied when this tab is active, overriding the default
   * `btnChange`. Lets a screen give a tab a different highlight colour
   * (e.g. the Orders trackers use turquoise, completed/rejected use grey).
   * Space-separated string; falls back to `btnChange` when unset.
   */
  activeClass?: string;
}

/** Sub-pixel slack before an edge counts as having tabs scrolled past it. */
const EDGE_TOLERANCE_PX = 1;

@Component({
  selector: 'app-navigation-tabs',
  templateUrl: './navigation-tabs.component.html',
  styleUrls: ['./navigation-tabs.component.scss'],
})
export class NavigationTabsComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  @Input() tabs: NavigationTab[] = [];
  /**
   * Extra class(es) applied to every tab button of THIS instance — a
   * per-screen geometry hook. The Orders and Reservations status strips pass
   * 'px-5' to keep develop's wide buttons; screens that omit it keep the
   * compact default.
   */
  @Input() buttonClass = '';
  @Output() tabClick = new EventEmitter<NavigationTab>();

  /** The scrolling one-line strip, and the row of tab buttons inside it. */
  @ViewChild('strip', { static: true }) strip: ElementRef<HTMLElement>;
  @ViewChild('track', { static: true }) track: ElementRef<HTMLElement>;

  /**
   * Keys each tab by `value`, falling back to `<index>:<label>` so value-less
   * tabs sharing a label never collide. Keeps the buttons in place when a
   * parent hands over an equivalent array — iOS WebKit withholds a tap when
   * the strip is re-created during the hover dispatch that precedes it.
   * Parents should still bind a stable array rather than a getter: a new
   * reference re-runs ngOnChanges on every change-detection pass.
   */
  trackByTab: TrackByFunction<NavigationTab> = (
    index: number,
    tab: NavigationTab,
  ): string => tab?.value ?? `${index}:${tab?.label ?? ''}`;

  private stripResizeObserver?: ResizeObserver;
  private activeTabRevealed = false;
  private readonly onStripScroll = (): void => this.updateEdgeFades();

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    this.ensureDefaultActive();
  }

  ngOnChanges(): void {
    this.ensureDefaultActive();
  }

  /**
   * Scrolling and resizing only move the strip and toggle its fade classes,
   * so both are handled outside Angular: no change detection per scroll
   * event, and the tab buttons are never re-rendered while being tapped
   * (see `trackByTab`). The observer also fires once on first layout, which
   * is when the selected tab is brought into view.
   */
  ngAfterViewInit(): void {
    const strip = this.strip.nativeElement;
    this.ngZone.runOutsideAngular(() => {
      strip.addEventListener('scroll', this.onStripScroll, { passive: true });
      this.stripResizeObserver = new ResizeObserver(() => this.onStripLayout());
      this.stripResizeObserver.observe(strip);
      this.stripResizeObserver.observe(this.track.nativeElement);
    });
  }

  ngOnDestroy(): void {
    this.strip.nativeElement.removeEventListener('scroll', this.onStripScroll);
    this.stripResizeObserver?.disconnect();
  }

  onTabClick(tab: NavigationTab) {
    this.tabClick.emit(tab);
  }

  hasRouterLink(tab: NavigationTab): boolean {
    return tab.routerLink !== undefined && tab.routerLink !== null;
  }

  // TODO: builds a fresh UrlTree per router-driven tab on every
  // change-detection pass; memoize per tab if it shows up in profiling.
  isTabHighlighted(tab: NavigationTab): boolean {
    if (tab.activeCondition) {
      return true;
    }
    return this.isRouterTabActive(tab);
  }

  private isRouterTabActive(tab: NavigationTab): boolean {
    if (!tab.routerLink) {
      return false;
    }
    const tabUrlTree = this.toUrlTree(tab.routerLink);
    if (!tabUrlTree) {
      return false;
    }
    return this.router.isActive(
      tabUrlTree,
      this.toIsActiveMatchOptions(tab.routerLinkActiveOptions),
    );
  }

  private toUrlTree(routerLink: string | any[]): UrlTree | null {
    if (Array.isArray(routerLink)) {
      return this.router.createUrlTree(routerLink, {
        relativeTo: this.activatedRoute,
      });
    }
    if (typeof routerLink === 'string') {
      if (routerLink.trim().length === 0) {
        return this.router.createUrlTree([], {
          relativeTo: this.activatedRoute,
        });
      }
      if (routerLink.startsWith('/')) {
        return this.router.parseUrl(routerLink);
      }
      return this.router.createUrlTree([routerLink], {
        relativeTo: this.activatedRoute,
      });
    }
    return null;
  }

  private toIsActiveMatchOptions(
    options?: { exact: boolean } | IsActiveMatchOptions,
  ): IsActiveMatchOptions {
    if (!options) {
      return {
        paths: 'subset',
        queryParams: 'subset',
        matrixParams: 'subset',
        fragment: 'ignored',
      };
    }
    if ('exact' in options) {
      return options.exact
        ? {
            paths: 'exact',
            queryParams: 'exact',
            matrixParams: 'exact',
            fragment: 'ignored',
          }
        : {
            paths: 'subset',
            queryParams: 'subset',
            matrixParams: 'subset',
            fragment: 'ignored',
          };
    }
    return options;
  }

  private onStripLayout(): void {
    if (this.strip.nativeElement.clientWidth === 0) {
      // Hidden (Settings hides the strip while the schedule editor is open):
      // the browser drops the scroll offset, so reveal the selected tab again
      // once the strip is shown.
      this.activeTabRevealed = false;
      return;
    }
    if (!this.activeTabRevealed) {
      this.activeTabRevealed = this.revealActiveTab();
    }
    this.updateEdgeFades();
  }

  /**
   * Centres the selected tab in the strip when it is not fully visible.
   * Returns false while no tab is selected yet, so the next layout pass
   * tries again. scrollLeft is computed rather than delegated to
   * `scrollIntoView({ inline: 'center' })`, whose options form iOS 13 does
   * not support (it would scroll the page vertically); same arithmetic as
   * StorePageComponent.centerCategoryNavOn. The browser clamps the value.
   */
  private revealActiveTab(): boolean {
    const strip = this.strip.nativeElement;
    const activeTab =
      this.track.nativeElement.querySelector<HTMLElement>(':scope > .active');
    if (!activeTab) {
      return false;
    }
    const stripRect = strip.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();
    if (tabRect.left < stripRect.left || tabRect.right > stripRect.right) {
      strip.scrollLeft +=
        tabRect.left - stripRect.left - (stripRect.width - tabRect.width) / 2;
    }
    return true;
  }

  /**
   * Fades an edge of the strip while tabs are scrolled past it. Read from the
   * position of the row against the strip rather than from scrollLeft, whose
   * sign and origin differ in right-to-left layouts.
   */
  private updateEdgeFades(): void {
    const strip = this.strip.nativeElement;
    const stripRect = strip.getBoundingClientRect();
    const trackRect = this.track.nativeElement.getBoundingClientRect();
    strip.classList.toggle(
      'fade-left',
      trackRect.left < stripRect.left - EDGE_TOLERANCE_PX,
    );
    strip.classList.toggle(
      'fade-right',
      trackRect.right > stripRect.right + EDGE_TOLERANCE_PX,
    );
  }

  /**
   * Guarantee that in-page (non-router) tab sets show a highlighted tab on
   * initial render. When no tab is router-driven and none has been marked
   * active by the parent, default the first visible tab to active. Pages
   * that drive highlighting through `routerLinkActive` (any tab carries a
   * `routerLink`) are left untouched, and a tab the parent has already
   * pre-selected (e.g. a deep link landing on a non-first tab) is preserved.
   *
   * Without this, pages whose tabs have no `routerLink` and whose parent
   * only sets `activeCondition` on `(tabClick)` rendered with no tab
   * highlighted until the user clicked one (regression from the migration
   * of bespoke per-page tab buttons onto this shared component).
   */
  private ensureDefaultActive(): void {
    if (!this.tabs?.length) {
      return;
    }
    const isRouterDriven = this.tabs.some((tab) => !!tab.routerLink);
    if (isRouterDriven) {
      return;
    }
    const alreadyActive = this.tabs.some((tab) => tab.activeCondition);
    if (alreadyActive) {
      return;
    }
    const firstVisible = this.tabs.find((tab) => !tab.hidden);
    if (firstVisible) {
      firstVisible.activeCondition = true;
    }
  }
}
